import Button from "@mui/material/Button";
import { Box, Stack } from "@mui/system";
import { Alert, Autocomplete, TextField, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import { setCreateQueryFormData } from "../../../../../features/dataStoreReqSlice";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useEffect } from "react";
import Editor from "react-simple-code-editor";
import Prism from "prismjs";
import "prismjs/components/prism-sql"; // load SQL syntax
import "prismjs/themes/prism.css"; // or your theme
import {
  getCurrentUtcTime,
  handleChangeHelper,
  handleFormatHelper,
} from "../../DataStoreHelpers/dataStoreHelpers";
import { selectUserData } from "../../../../../features/userSlice";

const CreateInfo = () => {
  const dispatch = useDispatch();
  const formData = useSelector(
    (state: any) => state.dataStoreReq.createQueryFormData
  );
  const { hasError, queryModules } = useSelector(
    (state: any) => state.dataStoreReq
  );

  const databaselist = [
    { label: "Google Bigquery", value: "google_bq" },
    { label: "MySQL", value: "central" },
  ];

  const requestTypeList = ["Business Insight", "Data Anomaly", "Report"];

  const handleChange = (key: string, value: any) => {
    handleChangeHelper(dispatch, setCreateQueryFormData, key, value);
  };

  const userEmail = useSelector(selectUserData)?.data?.user_details?.email;

  // Set created_by and created fields on initial load
  useEffect(() => {
    // Only set these values if they don't already exist (for initial load)
    if (!formData.created_by && userEmail) {
      handleChange("created_by", userEmail);
    }

    if (!formData.created) {
      handleChange("created", getCurrentUtcTime());
    }
    // eslint-disable-next-line
  }, [userEmail, formData.created_by, formData.created]);

  const handleFormat = () => {
    handleFormatHelper(formData.requestSql, (updatedSql) => {
      handleChange("requestSql", updatedSql);
    });
  };

  return (
    <Box>
      <Grid container spacing={2} mt={2}>
        <Grid size={4}>
          <TextField
            required
            id="outlined-required"
            label="Query Name"
            fullWidth
            size="small"
            color="secondary"
            value={formData.queryName}
            onChange={(e) => handleChange("queryName", e.target.value)}
            placeholder="How you like the Query to be called"
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />
        </Grid>
        <Grid size={4}>
          <Autocomplete
            disablePortal={false}
            options={databaselist}
            value={formData.database}
            onChange={(_, newValue) => handleChange("database", newValue)}
            getOptionLabel={(option) => option?.label || ""}
            renderInput={(params) => (
              <TextField
                required
                {...params}
                placeholder="Please select a database from the list"
                label="Database"
                fullWidth
                size="small"
                color="secondary"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            )}
          />
        </Grid>
        <Grid size={4}>
          <Autocomplete
            disablePortal={false}
            options={requestTypeList}
            value={formData.type}
            onChange={(_, newValue) => handleChange("type", newValue)}
            renderInput={(params) => (
              <TextField
                required
                {...params}
                placeholder="Please select request type from the list"
                label="Request Type"
                fullWidth
                size="small"
                color="secondary"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={2}>
        <Grid size={6}>
          <Autocomplete
            multiple
            size="small"
            disablePortal={false}
            options={queryModules}
            value={formData.modules || []}
            onChange={(_, newValue) => handleChange("modules", newValue)}
            renderInput={(params) => (
              <TextField
                required
                {...params}
                placeholder="Please select a module from the list"
                label="Modules"
                fullWidth
                size="small"
                color="secondary"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            )}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            id="outlined-required"
            label="Confluence URL"
            fullWidth
            size="small"
            color="secondary"
            value={formData.confluence_url}
            onChange={(e) => handleChange("confluence_url", e.target.value)}
            placeholder="Confluence URL"
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={2}>
        <Grid size={12}>
          <TextField
            required
            color="secondary"
            size="medium"
            fullWidth
            id="outlined-multiline-static"
            label="Query Description"
            multiline
            rows={4}
            value={formData.queryDescription}
            onChange={(e) => handleChange("queryDescription", e.target.value)}
            placeholder="What is the purpose of this SQL query"
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />
        </Grid>
        <Grid size={12}>
          <div
            style={{
              position: "relative",
              display: "inline-block",
              width: "100%",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "-8px",
                left: "8px",
                background: "#f5f5f5",
                padding: "2px 6px",
                fontSize: "12px",
                borderRadius: "4px",
                zIndex: 1,
              }}
            >
              Request SQL
            </div>

            <Box
              sx={{
                height: "300px", // Set fixed height here
                minHeight: "300px", // cannot go smaller than this
                width: "100%", // always full width
                resize: "vertical", // only allow vertical resize
                overflow: "auto", //
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            >
              <Editor
                value={formData.requestSql}
                onValueChange={(val) => handleChange("requestSql", val)}
                highlight={(code) =>
                  Prism.highlight(code, Prism.languages.sql, "sql")
                }
                padding={10}
                style={{
                  fontFamily: '"Fira code", "Fira Mono", monospace',
                  fontSize: 14,
                  minHeight: "100%", // Make sure editor fills container
                  whiteSpace: "pre-wrap",
                  wordWrap: "break-word",
                }}
              />
            </Box>
            {hasError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Something went wrong. Please check your inputs and SQL query for
                errors, then try again.
              </Alert>
            )}
          </div>

          <Stack spacing={1} direction="row" alignItems="center" mt={1} gap={2}>
            <Button
              color="secondary"
              variant="outlined"
              endIcon={<FormatListNumberedIcon />}
              onClick={handleFormat}
            >
              Format
            </Button>
            <Stack>
              <Typography
                variant="body2"
                sx={{ fontSize: "12px" }}
                color="#00000099"
              >
                Do not use snippets or comments in the SQL syntax. Do not use
                constructions like FORMAT(\“%d\“, CAST(floor(additional_2)) (or
                other constructions using \“%d\")).
              </Typography>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CreateInfo;
